import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api, ApiError } from '@/lib/api';
import { useEmailAudience, useEmailTemplates, type Audience } from '@/lib/email';
import { MERGE_FIELDS, mergeValues, renderMerge, unknownTokens } from '@/lib/merge';
import { ErrorNote, Spinner } from '@/components/Layout';
import { EmailPreview } from '@/components/admin/EmailPreview';
import { RecipientEditor, useRecipients } from '@/components/RecipientEditor';

interface SendResult { ok: true; batch_id: string; recipient_count: number; sent_count: number; failed_count: number }

/**
 * Write an email, preview it with a real recipient's details, and send it.
 *   admin: to a person, household, dojo, all current members, or chosen people; replies go to the Membership Secretary.
 *   instructor: to their own dojo or one of its students; replies go to them.
 * Who may send is checked by the page and again by the worker and database.
 */
export function EmailCompose({ mode, given, myDojos = [], replyTo, back, sentLink }: {
  mode: 'admin' | 'instructor'; given: Audience | null; myDojos?: { id: string; name: string }[]; replyTo?: string | null;
  back: { to: string; label: string }; sentLink: (batchId: string) => string;
}) {
  const admin = mode === 'admin';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const templates = useEmailTemplates();
  const [picked, setPicked] = useState<Audience | null>(!admin && myDojos.length === 1 ? { kind: 'dojo', id: myDojos[0]!.id } : null);
  const editable = useRecipients(admin ? given : null);   // admins can change who it goes to
  const audience = admin ? editable.audience : given ?? picked;
  const recipients = useEmailAudience(admin ? null : audience, 'instructor_email_audience');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sample, setSample] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);   // the batch id, made when the admin chooses Send
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<SendResult | null>(null);
  const [saveName, setSaveName] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const list = admin ? editable.rows : recipients.data ?? [];
  const who = list[Math.min(sample, Math.max(0, list.length - 1))];
  const values = who ? mergeValues(who) : Object.fromEntries(MERGE_FIELDS.map(f => [f.key, f.sample])) as ReturnType<typeof mergeValues>;
  const unknown = useMemo(() => unknownTokens(`${subject}\n${body}`), [subject, body]);

  if (result) return (
    <div className="card mx-auto max-w-xl space-y-3">
      <h2 className="text-xl font-bold">{result.failed_count ? 'Sent, with problems' : 'Sent'}</h2>
      <p>{result.sent_count} of {result.recipient_count} {result.recipient_count === 1 ? 'email' : 'emails'} sent.{result.failed_count > 0 && <strong className="text-brand-700"> {result.failed_count} could not be sent; see the list to send them again.</strong>}</p>
      <div className="flex gap-2"><Link to={sentLink(result.batch_id)} className="btn-primary">See who it went to</Link><Link to={back.to} className="btn-ghost border border-stone-300">{back.label}</Link></div>
    </div>
  );

  function insert(key: string) {
    const el = bodyRef.current; const token = `{{${key}}}`;
    if (!el) { setBody(body + token); return; }
    const at = el.selectionStart ?? body.length;
    setBody(body.slice(0, at) + token + body.slice(el.selectionEnd ?? at));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = at + token.length; });
  }
  function applyTemplate(id: string) {
    setTemplateId(id);
    const t = templates.data?.find(x => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body_text); }
  }
  async function saveTemplate() {
    setError('');
    const existing = templates.data?.find(t => t.id === templateId);
    const { data, error } = existing && !saveName
      ? await supabase.from('email_template').update({ subject, body_text: body }).eq('id', existing.id).select('id').single()
      : await supabase.from('email_template').insert({ name: saveName.trim(), subject, body_text: body }).select('id').single();
    if (error) { setError(/duplicate/.test(error.message) ? 'There is already a template with that name.' : `Could not save the template: ${error.message}`); return; }
    setTemplateId(data.id); setSaveName('');
    await qc.invalidateQueries({ queryKey: ['email-templates'] });
  }
  async function send() {
    if (!audience || !confirming) return;
    setBusy(true); setError('');
    try {
      const r = await api<SendResult>(admin ? '/admin/email/send' : '/instructor/email/send', { method: 'POST', body: { batch_id: confirming, subject, body_text: body, audience, template_id: templateId || undefined } });
      setResult(r);
      await Promise.all([qc.invalidateQueries({ queryKey: ['email-batches'] }), qc.invalidateQueries({ queryKey: ['admin-person-emails'] }), qc.invalidateQueries({ queryKey: ['admin-household-emails'] })]);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? 'You can only email your own dojo.' : e instanceof ApiError && e.status === 409 ? 'This email has already been sent.' : e instanceof ApiError && e.code === 'no_recipients' ? 'Nobody in this group has an email address.' : 'Could not send. Nothing was sent; please try again.');
    } finally { setBusy(false); setConfirming(null); }
  }

  const ready = !!audience && list.length > 0 && subject.trim() && body.trim();
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={back.to} className="text-sm text-ink-500 hover:underline">← {back.label}</Link>
      </div>

      <section className="card space-y-3">
        <h2 className="font-semibold">To</h2>
        {!given && !admin && myDojos.length > 1 && (
          <select className="input w-auto" aria-label="Which dojo" value={picked?.kind === 'dojo' ? picked.id : ''} onChange={e => setPicked(e.target.value ? { kind: 'dojo', id: e.target.value } : null)}>
            <option value="">Choose a dojo…</option>{myDojos.map(d => <option key={d.id} value={d.id}>Everyone at {d.name}</option>)}
          </select>
        )}
        {admin ? <RecipientEditor r={editable} note="Anyone without their own email address is reached through their account holder, and each address gets one copy. Replies go to the Membership Secretary." />
          : audience && (recipients.isLoading ? <Spinner /> : recipients.error ? <ErrorNote>Could not work out who this goes to.</ErrorNote> : (
            <div className="space-y-2">
              <p className="text-[15px]"><strong>{list.length} {list.length === 1 ? 'recipient' : 'recipients'}</strong></p>
              <ul className="flex flex-wrap gap-1.5">{list.map(x => <li key={x.to_email} className="rounded-sm bg-stone-100 px-2 py-0.5 text-sm"><span className="font-semibold">{x.first_name ?? x.to_email}</span> <span className="text-ink-500">{x.to_email}</span></li>)}</ul>
              <p className="text-sm text-ink-500">Anyone without their own email address is reached through their account holder, and each address gets one copy. Replies come to you{replyTo ? ` at ${replyTo}` : ''}.</p>
            </div>
          ))}
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="block min-w-[200px] flex-1"><span className="label">Start from a template</span>
              <select className="input" value={templateId} onChange={e => applyTemplate(e.target.value)}>
                <option value="">Blank</option>{(templates.data ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></label>
          </div>
          <label className="block"><span className="label">Subject</span><input className="input" maxLength={150} value={subject} onChange={e => setSubject(e.target.value)} /></label>
          <div>
            <div className="mb-1 flex flex-wrap items-center gap-1 text-xs"><span className="text-ink-500">Insert:</span>{MERGE_FIELDS.map(f => <button key={f.key} type="button" className="rounded-sm border border-stone-300 px-2 py-0.5 hover:bg-stone-100" onClick={() => insert(f.key)}>{f.label}</button>)}</div>
            <label className="block"><span className="sr-only">Message</span><textarea ref={bodyRef} rows={14} className="input" maxLength={10000} value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message. Use Insert to add each person's own details." /></label>
          </div>
          {unknown.length > 0 && <p className="text-sm text-brand-700">Not a field we know: {unknown.map(k => `{{${k}}}`).join(', ')}. It will be sent as typed.</p>}
          {admin && <div className="flex flex-wrap items-center gap-2 border-t border-stone-100 pt-3 text-sm">
            {templateId ? <button type="button" className="btn-ghost border border-stone-300 text-sm" disabled={!subject || !body} onClick={saveTemplate}>Update template</button> : null}
            <input className="input w-48 py-1.5 text-sm" placeholder="New template name" value={saveName} onChange={e => setSaveName(e.target.value)} />
            <button type="button" className="btn-ghost border border-stone-300 text-sm" disabled={!saveName.trim() || !subject || !body} onClick={saveTemplate}>Save as template</button>
          </div>}
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-semibold">Preview</h2>
            {list.length > 1 && <select className="input w-auto py-1 text-sm" aria-label="Preview for" value={sample} onChange={e => setSample(Number(e.target.value))}>{list.slice(0, 20).map((r, i) => <option key={r.to_email} value={i}>{r.first_name} ({r.to_email})</option>)}</select>}
          </div>
          <EmailPreview to={who?.to_email} subject={renderMerge(subject, values)} body={renderMerge(body, values)} />
        </section>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {confirming ? (
        <div className="card flex flex-wrap items-center justify-between gap-3 border-brand-200">
          <p>Send <strong>{subject}</strong> to <strong>{list.length} {list.length === 1 ? 'address' : 'addresses'}</strong> now? This cannot be undone.</p>
          <div className="flex gap-2"><button className="btn-primary" disabled={busy} onClick={send}>{busy ? 'Sending…' : 'Send now'}</button><button className="btn-ghost border border-stone-300" disabled={busy} onClick={() => setConfirming(null)}>Not yet</button></div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <button className="btn-ghost border border-stone-300" onClick={() => navigate(-1)}>Cancel</button>
          <button className="btn-primary" disabled={!ready} onClick={() => setConfirming(crypto.randomUUID())}>Send to {list.length || '…'}</button>
        </div>
      )}
    </div>
  );
}
